/**
 * Azure AD Authentication Service
 *
 * Handles authentication via Microsoft Azure AD / Entra ID using MSAL.js.
 * Uses the redirect flow for authentication (standard for enterprise apps).
 *
 * Security considerations:
 * - Uses PKCE for authorization code flow
 * - Access tokens stored in memory
 * - Supports silent token renewal
 */

import {
  PublicClientApplication,
  Configuration,
  AuthenticationResult,
  AccountInfo,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { azureADConfig, isAzureADConfigured } from '@/lib/auth-config';
import { logInfo, logError, logWarn } from '@/lib/logger';

const CONTEXT = 'AzureADAuthService';

// Storage keys
const TOKEN_STORAGE_KEY = 'cdb_azure_ad_tokens';

interface StoredTokens {
  accessToken: string;
  accessTokenExpiry: number;
}

// Token storage (in memory, with localStorage backup)
let accessToken: string | null = null;
let accessTokenExpiry: number | null = null;
let currentAccount: AccountInfo | null = null;

// MSAL instance
let msalInstance: PublicClientApplication | null = null;
let msalInitPromise: Promise<PublicClientApplication> | null = null;
let redirectResult: AuthenticationResult | null = null;

// Proaktiver Token-Refresh Timer
let tokenRefreshTimer: number | null = null;
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 Minuten vor Ablauf

// In-flight refreshAccessToken guard. Multiple callers (proactive timer,
// tryRestoreAuth, 401-retry in the API client) can race on
// `acquireTokenSilent`; MSAL tolerates it but emits duplicate
// auth-session-extended / auth-session-expired events and wastes
// roundtrips. Dedupe by returning the same in-flight promise.
let refreshPromise: Promise<string | null> | null = null;

/**
 * Get MSAL configuration.
 */
function getMsalConfig(): Configuration {
  // Use /organizations for multi-tenant login (allows users from any Azure AD tenant).
  // The BFF validates allowed tenants server-side via AZURE_AD_ALLOWED_TENANTS.
  const authority = 'https://login.microsoftonline.com/organizations';

  return {
    auth: {
      clientId: azureADConfig.clientId,
      authority,
      redirectUri: azureADConfig.redirectUri,
      postLogoutRedirectUri: window.location.origin,
      knownAuthorities: ['login.microsoftonline.com'], // Required for multi-tenant
    },
    cache: {
      cacheLocation: 'localStorage',
    },
    system: {
      loggerOptions: {
        logLevel: 3, // Warning
        piiLoggingEnabled: false,
      },
    },
  };
}

/**
 * Initialize MSAL instance (singleton with proper async handling).
 */
async function getMsalInstance(): Promise<PublicClientApplication> {
  // Return existing initialized instance
  if (msalInstance && msalInitPromise) {
    return msalInitPromise;
  }

  // Return in-progress initialization
  if (msalInitPromise) {
    return msalInitPromise;
  }

  if (!isAzureADConfigured()) {
    throw new Error('Azure AD is not configured. Please set VITE_AZURE_AD_TENANT_ID and VITE_AZURE_AD_CLIENT_ID.');
  }

  // Create initialization promise to prevent race conditions
  msalInitPromise = (async () => {
    msalInstance = new PublicClientApplication(getMsalConfig());
    await msalInstance.initialize();

    // Handle redirect response after initialization
    try {
      const response = await msalInstance.handleRedirectPromise();
      if (response?.state === CONSENT_STATE) {
        // A Graph token from the consent page, not a sign-in: it must not
        // replace the session token the backends expect.
        settleConsent('granted');
      } else if (response) {
        redirectResult = response;
        handleAuthResponse(response);
      }
    } catch (error) {
      // Declining or "needs admin approval" comes back as an error response.
      if (!settleConsent('refused')) {
        logError(error instanceof Error ? error : new Error(String(error)), {
          context: CONTEXT,
          message: 'Error handling redirect',
        });
      }
    }

    return msalInstance;
  })();

  return msalInitPromise;
}

/**
 * Schedule proactive token refresh before expiry.
 * Fires an `auth-session-expiring` event at the warning threshold
 * so the UI can show a warning before the session expires.
 */
function scheduleTokenRefresh(): void {
  // Clear existing timer
  if (tokenRefreshTimer !== null) {
    window.clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }

  if (!accessTokenExpiry) return;

  const now = Date.now();
  const timeUntilExpiry = accessTokenExpiry - now;
  const refreshTime = Math.max(0, timeUntilExpiry - TOKEN_REFRESH_BUFFER_MS);

  logInfo({ context: CONTEXT }, `Scheduling token refresh in ${Math.floor(refreshTime / 1000)}s`);

  tokenRefreshTimer = window.setTimeout(async () => {
    // Notify UI that session is about to expire
    window.dispatchEvent(new CustomEvent('auth-session-expiring'));
    logInfo({ context: CONTEXT }, 'Proactive token refresh triggered');

    // forceRefresh:true here is load-bearing. Without it MSAL returns the
    // cached token (same expiresOn), `handleAuthResponse` re-schedules with
    // `refreshTime = max(0, 5min - 5min) = 0`, the timer fires immediately,
    // and the user sees the expiry banner stay visible forever. With
    // forceRefresh we hit Azure AD for a fresh token with a new expiresOn.
    const newToken = await refreshAccessToken(true);
    if (newToken) {
      logInfo({ context: CONTEXT }, 'Proactive token refresh successful');
      // Dismiss the warning — session was extended successfully
      window.dispatchEvent(new CustomEvent('auth-session-extended'));
    } else {
      logWarn({ context: CONTEXT }, 'Proactive token refresh failed - user may need to re-login');
    }
  }, refreshTime);
}

/**
 * Stop token refresh timer.
 */
function stopTokenRefresh(): void {
  if (tokenRefreshTimer !== null) {
    window.clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
    logInfo({ context: CONTEXT }, 'Token refresh timer stopped');
  }
}

/**
 * Handle authentication response.
 */
function handleAuthResponse(response: AuthenticationResult): void {
  if (response.accessToken) {
    accessToken = response.accessToken;
    accessTokenExpiry = response.expiresOn?.getTime() || Date.now() + 3600000;
    currentAccount = response.account;
    saveTokensToStorage();
    // Dismiss any active expiry warning — session was refreshed
    window.dispatchEvent(new CustomEvent('auth-session-extended'));
    scheduleTokenRefresh(); // ← NEU: Timer planen
    logInfo({ context: CONTEXT }, `Authentication successful for ${response.account?.username}`);
  }
}

/**
 * Save tokens to localStorage.
 */
function saveTokensToStorage(): void {
  if (accessToken && accessTokenExpiry) {
    const tokens: StoredTokens = {
      accessToken,
      accessTokenExpiry,
    };
    try {
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
    } catch {
      // Ignore storage errors
    }
  }
}

/**
 * Load tokens from localStorage.
 */
function loadTokensFromStorage(): boolean {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) return false;

    const tokens: StoredTokens = JSON.parse(stored);
    const now = Date.now();

    if (tokens.accessTokenExpiry && now < tokens.accessTokenExpiry - 60000) {
      accessToken = tokens.accessToken;
      accessTokenExpiry = tokens.accessTokenExpiry;
      scheduleTokenRefresh(); // ← NEU: Timer planen nach Restore
      logInfo({ context: CONTEXT }, 'Tokens restored from localStorage');
      return true;
    }

    localStorage.removeItem(TOKEN_STORAGE_KEY);
    return false;
  } catch {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    return false;
  }
}

/**
 * Clear tokens from storage.
 */
function clearTokensFromStorage(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // Ignore errors
  }
}

/**
 * User info returned after authentication.
 */
export interface AzureADUserInfo {
  userId: string;
  email: string;
  name?: string;
  username: string;
}

/**
 * Login result with token and user info.
 */
export interface LoginResult {
  accessToken: string;
  expiresIn: number;
  user: AzureADUserInfo;
}

/**
 * Login with Azure AD using redirect.
 * Redirect is used instead of popup to avoid COOP warnings.
 */
export async function loginWithRedirect(): Promise<void> {
  logInfo({ context: CONTEXT }, 'Initiating Azure AD redirect login');

  const msal = await getMsalInstance();

  await msal.loginRedirect({
    scopes: azureADConfig.scopes,
    prompt: 'select_account',
  });
}

// ── Consent for a connector (cdb_tqs#1785) ──────────────────────────────────
//
// A tenant that has not yet allowed the app to read mail gets Microsoft's own
// consent page, where an admin can consent on behalf of the organization. The
// redirect starts and ends on the callback page, so the answer is settled
// there; sessionStorage carries it across the full-page redirect.

const CONSENT_STATE = 'connector-consent';
const CONSENT_PENDING_KEY = 'cdb-consent-pending';
const CONSENT_OUTCOME_KEY = 'cdb-consent-outcome';

export type ConsentOutcome = 'granted' | 'refused';

/** Record the outcome if a consent redirect was pending; false otherwise. */
function settleConsent(outcome: ConsentOutcome): boolean {
  try {
    if (!sessionStorage.getItem(CONSENT_PENDING_KEY)) return false;
    sessionStorage.removeItem(CONSENT_PENDING_KEY);
    sessionStorage.setItem(CONSENT_OUTCOME_KEY, outcome);
    logInfo({ context: CONTEXT }, `Connector consent ${outcome}`);
    return true;
  } catch {
    return false;
  }
}

/** Send the person to Microsoft's consent page for `scopes`, in their own tenant. */
export async function requestConsentRedirect(scopes: string[]): Promise<void> {
  const msal = await getMsalInstance();
  const account = currentAccount || msal.getAllAccounts()[0];
  sessionStorage.setItem(CONSENT_PENDING_KEY, '1');
  sessionStorage.removeItem(CONSENT_OUTCOME_KEY);
  await msal.acquireTokenRedirect({
    scopes,
    account,
    authority: account?.tenantId
      ? `https://login.microsoftonline.com/${account.tenantId}`
      : undefined,
    prompt: 'consent',
    state: CONSENT_STATE,
    redirectStartPage: azureADConfig.redirectUri,
  });
}

/** The outcome of the last consent redirect, once; null when there was none. */
export async function takeConsentOutcome(): Promise<ConsentOutcome | null> {
  await getMsalInstance();
  try {
    const outcome = sessionStorage.getItem(CONSENT_OUTCOME_KEY);
    sessionStorage.removeItem(CONSENT_OUTCOME_KEY);
    return outcome === 'granted' || outcome === 'refused' ? outcome : null;
  } catch {
    return null;
  }
}

/**
 * Check if we have a pending redirect response and handle it.
 * Returns LoginResult if redirect was successful, null otherwise.
 */
export async function handleRedirectResult(): Promise<LoginResult | null> {
  // Ensure MSAL is initialized and redirect is processed
  await getMsalInstance();

  // Use the cached redirect result from getMsalInstance()
  if (redirectResult && redirectResult.accessToken) {
    const response = redirectResult;
    // Clear the cached result so it's only processed once
    redirectResult = null;

    logInfo({ context: CONTEXT }, `Redirect login successful for ${response.account?.username}`);

    return {
      accessToken: response.accessToken,
      expiresIn: Math.floor((response.expiresOn?.getTime() || Date.now() + 3600000 - Date.now()) / 1000),
      user: {
        userId: response.account?.localAccountId || response.uniqueId,
        email: response.account?.username || '',
        name: response.account?.name,
        username: response.account?.username || '',
      },
    };
  }

  return null;
}

/**
 * Get the current access token.
 * Attempts silent renewal if token is expired.
 */
export function getAccessToken(): string | null {
  if (!accessToken) {
    return null;
  }

  // Check if token is expired (with 60 second buffer)
  if (accessTokenExpiry && Date.now() > accessTokenExpiry - 60000) {
    logWarn({ context: CONTEXT }, 'Access token expired or expiring soon');
    return null;
  }

  return accessToken;
}

/**
 * Refresh the access token silently. Concurrent callers share one
 * in-flight promise so MSAL only sees a single acquireTokenSilent.
 *
 * `forceRefresh=true` bypasses MSAL's token cache and asks Azure AD for a
 * brand-new token. Use it whenever the goal is to extend the session
 * past the current expiry (proactive timer, "Sitzung verlängern" button).
 * Default is `false` — fine for restore-on-page-load and 401-retry paths
 * where the cached token (if still valid) is the right answer.
 */
export async function refreshAccessToken(forceRefresh = false): Promise<string | null> {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = doRefreshAccessToken(forceRefresh).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function doRefreshAccessToken(forceRefresh: boolean): Promise<string | null> {
  logInfo({ context: CONTEXT }, `Attempting silent token refresh (forceRefresh=${forceRefresh})`);

  const msal = await getMsalInstance();
  const accounts = msal.getAllAccounts();

  if (accounts.length === 0) {
    logWarn({ context: CONTEXT }, 'No accounts found for silent refresh');
    return null;
  }

  const account = currentAccount || accounts[0];

  try {
    const response = await msal.acquireTokenSilent({
      scopes: azureADConfig.scopes,
      account: account,
      forceRefresh,
    });

    handleAuthResponse(response);
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      logWarn({ context: CONTEXT }, 'Silent refresh failed, interaction required - logging out user');
      // Clear tokens - user needs to re-login
      clearTokens();
      // Fire event to trigger logout and redirect to login
      window.dispatchEvent(new CustomEvent('auth-session-expired'));
    } else {
      logError(error instanceof Error ? error : new Error(String(error)), {
        context: CONTEXT,
        message: 'Silent token refresh failed',
      });
      // Bei anderen Fehlern auch ausloggen zur Sicherheit
      clearTokens();
      window.dispatchEvent(new CustomEvent('auth-session-expired'));
    }
    return null;
  }
}

/**
 * The user's token with its expiry and account, for handing it to one of our
 * own products running in a desktop window (#1506). Refreshed when due; null
 * when there is no session. Never log what this returns.
 */
export async function getTokenForHandoff(): Promise<{
  accessToken: string;
  expiresOn: number;
  account: { id: string | null; name: string | null; username: string | null };
} | null> {
  const token = getAccessToken() ?? (await refreshAccessToken());
  if (!token || !accessTokenExpiry) return null;
  // A session restored from localStorage never sets currentAccount; the
  // Composer refuses a handoff without the oid, so read it from MSAL's cache.
  const account = currentAccount ?? (await getMsalInstance()).getAllAccounts()[0] ?? null;
  return {
    accessToken: token,
    expiresOn: accessTokenExpiry,
    account: {
      id: account?.localAccountId ?? null,
      name: account?.name ?? null,
      username: account?.username ?? null,
    },
  };
}

/**
 * Check if user is authenticated.
 */
export function isAuthenticated(): boolean {
  return getAccessToken() !== null;
}

/**
 * Who the signed-in Microsoft account is, without a redirect. A reload has no
 * redirect result to read it from, yet the waiting room names the account.
 */
export async function getAccountInfo(): Promise<AzureADUserInfo | null> {
  const msal = await getMsalInstance();
  const account = currentAccount || msal.getAllAccounts()[0];
  if (!account) return null;
  return {
    userId: account.localAccountId,
    email: account.username,
    name: account.name,
    username: account.username,
  };
}

/**
 * Try to restore authentication from cache/storage.
 */
export async function tryRestoreAuth(): Promise<boolean> {
  // First try localStorage
  if (loadTokensFromStorage()) {
    return true;
  }

  // Try to get account from MSAL cache and acquire token silently
  try {
    const msal = await getMsalInstance();
    const accounts = msal.getAllAccounts();

    if (accounts.length > 0) {
      currentAccount = accounts[0];
      const token = await refreshAccessToken();
      return token !== null;
    }
  } catch (error) {
    logWarn({ context: CONTEXT }, 'Failed to restore auth from MSAL cache');
  }

  return false;
}

/**
 * Logout - clear all tokens and MSAL cache.
 * Uses redirect instead of popup to avoid COOP warnings.
 */
export async function logout(): Promise<void> {
  logInfo({ context: CONTEXT }, 'Logging out');

  clearTokens();

  try {
    const msal = await getMsalInstance();
    const accounts = msal.getAllAccounts();

    if (accounts.length > 0) {
      // Logout from Azure AD using redirect
      await msal.logoutRedirect({
        account: currentAccount || accounts[0],
        postLogoutRedirectUri: window.location.origin,
      });
    }
  } catch (error) {
    // Ignore logout errors - tokens are already cleared locally
    logWarn({ context: CONTEXT }, 'MSAL logout failed, local tokens cleared');
  }
}

/**
 * Invalidate the current access token without triggering a redirect
 * or clearing the MSAL account cache. The MSAL cache (refresh tokens,
 * account info) is preserved so the re-login can restore the session
 * including user settings.
 */
export function clearLocalSession(): void {
  logInfo({ context: CONTEXT }, 'Clearing local access token (no redirect, MSAL cache preserved)');
  stopTokenRefresh();
  accessToken = null;
  accessTokenExpiry = null;
  clearTokensFromStorage();
}

/**
 * Clear all auth state (tokens + MSAL cache) without triggering a redirect.
 * Removes only MSAL-related localStorage entries — app settings (cdb-*) are preserved.
 * Use when the user explicitly wants to re-login and should see the login page.
 */
export async function clearForRelogin(): Promise<void> {
  logInfo({ context: CONTEXT }, 'Clearing auth state for re-login (no redirect, app settings preserved)');
  clearTokens();

  // Use MSAL's own cache cleaner instead of a manual key scan. The previous
  // approach matched any key containing the client-id GUID — `String.includes`
  // is broad enough that any third-party storage embedding the same GUID
  // would get wiped. msalInstance.clearCache() only touches MSAL-owned
  // entries; app settings under `cdb-*` remain intact.
  if (msalInstance) {
    await msalInstance.clearCache();
  }
}

/**
 * Clear all stored tokens.
 */
function clearTokens(): void {
  stopTokenRefresh(); // ← NEU: Timer stoppen
  accessToken = null;
  accessTokenExpiry = null;
  currentAccount = null;
  clearTokensFromStorage();
}

// Note: Azure AD doesn't support ROPC for managed accounts.
// Signup is typically handled through Azure AD B2C or Azure AD admin.
// Password reset is handled through Azure AD portal.
